import type { SuperadminUser } from '../types';

export interface CreditElements {
  superadminUserCredits?: HTMLElement | null;
  superadminCreditBalance?: HTMLElement | null;
  superadminCreditStatus?: HTMLElement | null;
  superadminCreditRenewal?: HTMLElement | null;
  superadminAdjustCreditsBtn?: HTMLElement | null;
  superadminExtendSubscriptionBtn?: HTMLElement | null;
}

export function renderUserCreditInfo(
  userData: SuperadminUser | null,
  elements: CreditElements
): void {
  const {
    superadminUserCredits,
    superadminCreditBalance,
    superadminCreditStatus,
    superadminCreditRenewal,
    superadminAdjustCreditsBtn,
    superadminExtendSubscriptionBtn,
  } = elements;

  if (!superadminUserCredits) {
    return;
  }

  if (!userData) {
    superadminUserCredits.hidden = true;
    return;
  }

  superadminUserCredits.hidden = false;

  if (superadminCreditBalance) {
    const credits =
      typeof (userData as any).creditsRemaining === 'number' ? userData.creditsRemaining : 0;
    const ledger = ((userData as any).creditLedger || {}) as {
      grantedTotal?: number;
      consumedTotal?: number;
    };
    const granted = ledger?.grantedTotal ?? 0;
    const consumed = ledger?.consumedTotal ?? 0;
    const hasLedgerData = ledger && (granted > 0 || consumed > 0);
    superadminCreditBalance.textContent = `Credits: ${credits}`;
    superadminCreditBalance.title = hasLedgerData
      ? `Total Granted: ${granted} | Total Consumed: ${consumed}`
      : 'No ledger data available';
  }

  if (superadminCreditStatus) {
    const status = (userData as any).subscriptionStatus || 'inactive';
    const isActive = status === 'active';
    const badge = isActive ? '✓' : '✗';
    let statusLabel = isActive ? 'Subscription Active' : 'No Subscription';

    const expiresRaw = (userData as any).subscriptionExpiresAt;
    if (isActive && (typeof expiresRaw === 'string' || typeof expiresRaw === 'number')) {
      const expiresAt = new Date(expiresRaw as string | number);
      const now = new Date();
      const daysRemaining = Math.ceil(
        (expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
      );
      if (daysRemaining > 0) {
        statusLabel += ` (${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} left)`;
      } else if (daysRemaining === 0) {
        statusLabel += ' (expires today)';
      } else {
        statusLabel = 'Subscription Expired';
      }
    }

    superadminCreditStatus.textContent = `${badge} ${statusLabel}`;
    superadminCreditStatus.dataset.status = isActive ? 'active' : 'inactive';
  }

  if (superadminCreditRenewal) {
    const renewal = (userData as any).lastCreditRenewalAt as unknown;
    let label: string | null = null;
    if (renewal instanceof Date) {
      label = renewal.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } else if (typeof renewal === 'string' || typeof renewal === 'number') {
      const parsed = new Date(renewal);
      if (!isNaN(parsed.getTime())) {
        label = parsed.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });
      }
    }
    superadminCreditRenewal.textContent = label ? `Renewed: ${label}` : 'No renewal date';
  }

  if (superadminAdjustCreditsBtn) {
    superadminAdjustCreditsBtn.hidden = false;
  }
  if (superadminExtendSubscriptionBtn) {
    superadminExtendSubscriptionBtn.hidden = false;
  }
}
